import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  ChevronRight,
  FileArchive,
  Headset,
  Heart,
  LogIn,
  NotepadText,
  Package,
  Trash2,
  UserRound,
  Users,
  WalletMinimal,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useGetWalletContextsQuery } from "@/lib/api/apiSlice";
import { useShowInitialLoader } from "@/lib/hooks";
import { inter, philosopher } from "@/lib/fonts";
import { footerBg3Style, profile } from "@/assets";
import { routes } from "@/lib/constants";
import { UserType } from "muminsmart-utils";
import ButtonsHeader from "../components/common/ButtonsHeader";
import WalletBalance from "../components/common/WalletBalance";
import Footer from "../components/common/Footer";
import { ProfileLogoutButton } from "../components/profile/ProfileLogoutButton";
import ProfileSkeleton from "../components/common/skeletons/ProfileSkeleton";

export default function ProfilePage() {
  const { data: session, status } = useAuth();
  const { data: walletContexts, isLoading: isWalletLoading } =
    useGetWalletContextsQuery(undefined, {
      skip: status !== "authenticated",
    });

  const showWalletLoader = useShowInitialLoader(
    isWalletLoading,
    walletContexts,
  );

  const items = useMemo(() => {
    const menuItems: { label: string; icon: LucideIcon; link: string }[] = [
      {
        label: "My Profile",
        icon: UserRound,
        link: routes.profile.edit,
      },
      {
        label: "My Orders",
        icon: Package,
        link: routes.orders.index,
      },
      {
        label: "My Wallet",
        icon: WalletMinimal,
        link: routes.wallet.index,
      },
    ];

    if (session?.user?.type === UserType.User) {
      menuItems.push(
        {
          label: "Wishlist",
          icon: Heart,
          link: routes.wishlist,
        },
        {
          label: "My Referrals",
          icon: Users,
          link: routes.profile.myReferrals,
        },
        {
          label: "Register as seller",
          icon: LogIn,
          link: routes.seller.signup,
        },
      );
    }

    if (session?.user?.type === UserType.Seller) {
      menuItems.push({
        label: "Download Invoices",
        icon: FileArchive,
        link: routes.seller.invoices,
      });
    }

    menuItems.push(
      {
        label: "Help",
        icon: Headset,
        link: routes.help.index,
      },
      {
        label: "Privacy Policy",
        icon: NotepadText,
        link: routes.privacyPolicy.user,
      },
    );

    return menuItems;
  }, [session?.user?.type]);

  if (status === "loading" || showWalletLoader) {
    return <ProfileSkeleton />;
  }

  return (
    <>
      <ButtonsHeader heading="Profile" />
      <div className="flex items-center justify-start gap-4 px-4">
        <img
          src={profile}
          alt="Profile"
          className="object-cover rounded-full bg-[#D7EAF5] border-2 border-[#F2994A] m-1"
          width={100}
          height={100}
        />
        <div className="flex shrink flex-col items-start">
          <h1 className={`text-xl font-bold ${philosopher.className}`}>
            {session?.user?.fullName}
          </h1>
          <WalletBalance walletContexts={walletContexts ?? null} />
        </div>
      </div>
      <div
        className="p-6 pb-10 mt-4 rounded-t-3xl bg-white min-h-[calc(100vh-13.5rem)]"
        style={footerBg3Style}
      >
        {items.map((item) => (
          <Link
            to={item.link}
            key={item.label}
            className="flex items-center justify-between gap-4 border-b border-b-gray-100 pb-4 mb-4"
            prefetch="intent"
          >
            <div className="flex items-center gap-4">
              <item.icon strokeWidth={1.5} />
              <p className={`text-sm ${inter.className}`}>{item.label}</p>
            </div>
            <div>
              <ChevronRight strokeWidth={1} />
            </div>
          </Link>
        ))}
        {session?.user?.type === UserType.User ? (
          <Link
            to={routes.profile.deleteAccount}
            className="flex items-center justify-between gap-4 border-b border-b-gray-100 pb-4 mb-4"
            prefetch="intent"
          >
            <div className="flex items-center gap-4 text-destructive">
              <Trash2 strokeWidth={1.5} />
              <p className={`text-sm ${inter.className}`}>Delete Account</p>
            </div>
            <div>
              <ChevronRight strokeWidth={1} />
            </div>
          </Link>
        ) : null}
        <ProfileLogoutButton />
        <Footer />
      </div>
    </>
  );
}
